import { useAllTimeslotsSessionsQuery } from "api/queries/timeslotSession";
import { useForumDateFormatter } from "components/ForumDateFormatProvider";
import { Select, Space, Typography } from "components/styleguide";
import { TimeSlotSessionResponseModel, TimeSlotType } from "generated/api";
import { toNumber } from "lodash";
import { Dispatch, SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AllMealtimes } from "../../constants";

const { Text } = Typography;

interface MealtimeOptionProps {
  session?: number | typeof AllMealtimes;
  setSession: Dispatch<SetStateAction<number | typeof AllMealtimes | undefined>>;
  showAllMealtimesOption?: boolean;
}

const MealtimeOption = ({ session, setSession, showAllMealtimesOption = false }: MealtimeOptionProps) => {
  const { t } = useTranslation();
  const { formatDate } = useForumDateFormatter();

  const forumId = toNumber(useParams().id);

  const { data: { data: mealtimeSessions = [] } = {}, isLoading: isSessionsLoading } = useAllTimeslotsSessionsQuery(
    {
      forumId,
      timeSlotTypes: [TimeSlotType.Breakfast, TimeSlotType.Lunch, TimeSlotType.Dinner],
    },
  );

  const mealtimeOptions = useMemo(() => {
    const allMealtimesOption = { value: AllMealtimes, label: t("All Mealtimes") };
    const baseMealtimesOptions = [...mealtimeSessions]
      .sort((a, b) => a.startTime?.localeCompare(b.startTime as string) as number)
      .map(({ id, startTime, timeSlotType }: TimeSlotSessionResponseModel) => ({
        value: id as number,
        label: `${formatDate(startTime, { showTime: true })} - ${timeSlotType}`,
      }));

    return showAllMealtimesOption ? [allMealtimesOption, ...baseMealtimesOptions] : baseMealtimesOptions;
  }, [formatDate, mealtimeSessions, showAllMealtimesOption, t]);

  return (
    <Space direction={"vertical"}>
      <Text>{t("Mealtime")}</Text>
      <Select
        style={{ minWidth: "200px" }}
        dropdownStyle={{ minWidth: "250px" }}
        placeholder={t("Select mealtime")}
        options={mealtimeOptions}
        value={session}
        onChange={setSession}
        loading={isSessionsLoading}
        allowClear
        showSearch
        optionFilterProp={"label"}
      />
    </Space>
  );
};

export default MealtimeOption;
